/*
 * Назначение: типизированные обёртки над админским API.
 * Функционал: вызовы /api/admin/* с поддержкой выбора текущего проекта
 * через заголовок X-Project-Slug. Заголовок проставляется снаружи через
 * глобальный helper setAdminProjectSlug.
 */

import { api } from "./api";
import type {
  AdminUserCreatePayload,
  AdminUserDetailed,
  AdminUserUpdatePayload,
  DashboardOverview,
  DialogDetails,
  KnowledgeDocument,
  KnowledgeItem,
  KnowledgeItemCreate,
  KnowledgeItemUpdate,
  KnowledgeKind,
  Lead,
  LeadStatusUpdate,
  ModelTestResponse,
  PageOf,
  ProjectClonePayload,
  ProjectCreatePayload,
  ProjectDetails,
  ProjectGeneralUpdate,
  ProjectManager,
  ProjectManagerCreate,
  ProjectManagerUpdate,
  ProjectShort,
  ProjectUpdate,
  PromptPreview,
  DialogShort,
} from "./types";

let currentProjectSlug: string | null = null;

export function setAdminProjectSlug(slug: string | null): void {
  currentProjectSlug = slug;
}

/** Заголовок проекта; slug из ключа запроса важнее глобального — запрос со
 *  старым ключом не уйдёт с заголовком только что выбранного проекта. */
function projectHeaders(slug?: string | null): Record<string, string> {
  const s = slug ?? currentProjectSlug;
  return s ? { "X-Project-Slug": s } : {};
}

function buildQuery(params: Record<string, unknown> | object): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export const projectsApi = {
  list: () => api.get<ProjectShort[]>("/api/admin/projects"),
  current: () =>
    api.get<ProjectDetails>("/api/admin/projects/current", { headers: projectHeaders() }),
  create: (payload: ProjectCreatePayload) =>
    api.post<ProjectDetails>("/api/admin/projects", payload),
  clone: (payload: ProjectClonePayload) =>
    api.post<ProjectDetails>("/api/admin/projects/clone", payload),
  remove: (slug: string) => api.delete<void>(`/api/admin/projects/${slug}`),
  patchCurrent: (payload: ProjectGeneralUpdate) =>
    api.patch<ProjectDetails>("/api/admin/projects/current/general", payload, {
      headers: projectHeaders(),
    }),
  update: (payload: ProjectUpdate) =>
    api.patch<ProjectDetails>("/api/admin/projects/current", payload, {
      headers: projectHeaders(),
    }),
  /** providerId пустой — проверяем модель у любого доступного провайдера. */
  testModel: (model: string, prompt = "Скажи коротко привет.", providerId = "") =>
    api.post<ModelTestResponse>(
      "/api/admin/projects/current/test-model",
      { model, prompt, provider_id: providerId },
      { headers: projectHeaders() },
    ),
  promptPreview: (payload: {
    base_system_prompt?: string | null;
    system_prompt?: string | null;
  }) =>
    api.post<PromptPreview>(
      "/api/admin/projects/current/prompt-preview",
      payload,
      { headers: projectHeaders() },
    ),
  uploadAvatar: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.postForm<ProjectDetails>(
      "/api/admin/projects/current/avatar",
      fd,
      { headers: projectHeaders() },
    );
  },
  deleteAvatar: () =>
    api.delete<ProjectDetails>("/api/admin/projects/current/avatar", {
      headers: projectHeaders(),
    }),
};

export const managersApi = {
  list: () =>
    api.get<ProjectManager[]>("/api/admin/projects/current/managers", {
      headers: projectHeaders(),
    }),
  create: (payload: ProjectManagerCreate) =>
    api.post<ProjectManager>("/api/admin/projects/current/managers", payload, {
      headers: projectHeaders(),
    }),
  update: (id: string, payload: ProjectManagerUpdate) =>
    api.patch<ProjectManager>(`/api/admin/projects/current/managers/${id}`, payload, {
      headers: projectHeaders(),
    }),
  remove: (id: string) =>
    api.delete<void>(`/api/admin/projects/current/managers/${id}`, {
      headers: projectHeaders(),
    }),
};

export const knowledgeApi = {
  list: (kind?: KnowledgeKind) =>
    api.get<KnowledgeItem[]>(`/api/admin/knowledge${buildQuery({ kind })}`, {
      headers: projectHeaders(),
    }),
  create: (payload: KnowledgeItemCreate) =>
    api.post<KnowledgeItem>("/api/admin/knowledge", payload, { headers: projectHeaders() }),
  update: (id: string, payload: KnowledgeItemUpdate) =>
    api.patch<KnowledgeItem>(`/api/admin/knowledge/${id}`, payload, {
      headers: projectHeaders(),
    }),
  remove: (id: string) =>
    api.delete<void>(`/api/admin/knowledge/${id}`, { headers: projectHeaders() }),
  // Документы базы знаний
  listDocuments: () =>
    api.get<KnowledgeDocument[]>("/api/admin/knowledge/documents", {
      headers: projectHeaders(),
    }),
  uploadDocument: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.postForm<KnowledgeDocument>("/api/admin/knowledge/documents", fd, {
      headers: projectHeaders(),
    });
  },
  updateDocument: (id: string, include_in_context: boolean) =>
    api.patch<KnowledgeDocument>(
      `/api/admin/knowledge/documents/${id}`,
      { include_in_context },
      { headers: projectHeaders() },
    ),
  removeDocument: (id: string) =>
    api.delete<void>(`/api/admin/knowledge/documents/${id}`, {
      headers: projectHeaders(),
    }),
};

export interface DialogsListParams {
  page?: number;
  page_size?: number;
  channel?: string;
  status?: string;
  has_lead?: boolean;
  search?: string;
}

export const dialogsApi = {
  list: (params: DialogsListParams = {}, slug?: string | null) =>
    api.get<PageOf<DialogShort>>(
      `/api/admin/dialogs${buildQuery(params)}`,
      { headers: projectHeaders(slug) },
    ),
  get: (id: string, slug?: string | null) =>
    api.get<DialogDetails>(`/api/admin/dialogs/${id}`, { headers: projectHeaders(slug) }),
  remove: (id: string) =>
    api.delete<void>(`/api/admin/dialogs/${id}`, { headers: projectHeaders() }),
  bulkRemove: (payload: {
    ids?: string[];
    channel?: string;
    status?: string;
    has_lead?: boolean | null;
    search?: string;
    confirm?: boolean;
  }) =>
    api.post<{ deleted: number }>(`/api/admin/dialogs/bulk-delete`, payload, {
      headers: projectHeaders(),
    }),
  handoff: (id: string, reason?: string) =>
    api.post<DialogDetails>(`/api/admin/dialogs/${id}/handoff`, {
      reason: reason || null,
      generate_summary: true,
    }, { headers: projectHeaders() }),
  returnToBot: (id: string) =>
    api.post<DialogDetails>(`/api/admin/dialogs/${id}/return-to-bot`, {}, {
      headers: projectHeaders(),
    }),
  reply: (id: string, text: string) =>
    api.post<DialogDetails>(`/api/admin/dialogs/${id}/reply`, { text }, {
      headers: projectHeaders(),
    }),
  uploadAttachment: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.postForm<DialogDetails>(
      `/api/admin/dialogs/${id}/manager-attachment`,
      fd,
      { headers: projectHeaders() },
    );
  },
};

export interface LeadsListParams {
  page?: number;
  page_size?: number;
  channel?: string;
  status?: string;
  search?: string;
}

export const leadsApi = {
  list: (params: LeadsListParams = {}) =>
    api.get<PageOf<Lead>>(`/api/admin/leads${buildQuery(params)}`, {
      headers: projectHeaders(),
    }),
  update: (id: string, payload: LeadStatusUpdate) =>
    api.patch<Lead>(`/api/admin/leads/${id}`, payload, { headers: projectHeaders() }),
  /** Адрес для скачивания CSV. Заголовок X-Project-Slug отдаём через прокси-fetch ниже. */
  exportCsvUrl: () => "/api/admin/leads/export.csv",
};

export const dashboardApi = {
  overview: (period: number) =>
    api.get<DashboardOverview>(`/api/admin/dashboard${buildQuery({ period })}`, {
      headers: projectHeaders(),
    }),
};

export const usersApi = {
  list: () => api.get<AdminUserDetailed[]>("/api/admin/users"),
  create: (payload: AdminUserCreatePayload) =>
    api.post<AdminUserDetailed>("/api/admin/users", payload),
  update: (id: string, payload: AdminUserUpdatePayload) =>
    api.patch<AdminUserDetailed>(`/api/admin/users/${id}`, payload),
  resetPassword: (id: string, password: string) =>
    api.post<AdminUserDetailed>(`/api/admin/users/${id}/password`, { password }),
  remove: (id: string) => api.delete<void>(`/api/admin/users/${id}`),
};

// ============================================================================
// Интеграции (глобальные настройки на всю инсталляцию)
// ============================================================================

export interface TelegramProxyConfig {
  enabled: boolean;
  type: "http" | "socks5";
  host: string;
  port_http: number;
  port_socks5: number;
  username: string;
  password: string;
}

export interface TelegramTestResult {
  ok: boolean;
  bot_username: string | null;
  bot_id: number | null;
  detail: string | null;
}

export const integrationsApi = {
  getTelegramProxy: () =>
    api.get<TelegramProxyConfig>("/api/admin/integrations/telegram/proxy"),
  updateTelegramProxy: (payload: TelegramProxyConfig) =>
    api.put<TelegramProxyConfig>(
      "/api/admin/integrations/telegram/proxy",
      payload,
    ),
  testTelegram: (token?: string) =>
    api.post<TelegramTestResult>("/api/admin/integrations/telegram/test", {
      token: token || null,
    }),
  getAiProviders: () =>
    api.get<{ providers: AiProvider[] }>("/api/admin/integrations/ai-providers"),
  updateAiProviders: (providers: AiProvider[]) =>
    api.put<{ providers: AiProvider[] }>("/api/admin/integrations/ai-providers", {
      providers,
    }),
  getSmtp: () => api.get<SmtpConfig>("/api/admin/integrations/smtp"),
  updateSmtp: (payload: SmtpConfig) =>
    api.put<SmtpConfig>("/api/admin/integrations/smtp", payload),
  testSmtp: (to: string) =>
    api.post<{ ok: boolean; detail: string | null }>(
      "/api/admin/integrations/smtp/test",
      { to },
    ),
};

export interface SmtpConfig {
  enabled: boolean;
  host: string;
  port: number;
  username: string;
  password: string;
  from_addr: string;
  from_name: string;
  use_ssl: boolean;
}

export interface AiProvider {
  id?: string | null;
  name: string;
  api_key: string;
  base_url: string;
  /** Таймаут запроса к провайдеру, секунды (5..300). */
  timeout_seconds: number;
  enabled: boolean;
  order: number;
  has_key?: boolean;
  key_preview?: string;
}
